import React, { useState, useEffect } from 'react';
import {
  StopCircle, Zap, DollarSign, TrendingUp, TrendingDown,
  AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Loader2,
  Target, Users, Clock, BarChart3, Building2, ArrowRight,
  CheckCircle2, XCircle, Info, Sparkles
} from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { useCurrency } from '../utils/currencyUtils';

// =============================================================================
// EXECUTIVE RECOMMENDATIONS PANEL
// =============================================================================
// Surfaces the 4 Executive Recommendation APIs:
// 1. What Should Stop
// 2. What Should Accelerate
// 3. Value Analysis (Most/Least Valuable)
// 4. Funding Priorities
// =============================================================================

const RecommendationCard = ({ 
  title, 
  icon: Icon, 
  iconColor, 
  bgColor,
  items, 
  loading, 
  error,
  type,
  formatCurrency,
  currencySymbol,
  expanded,
  onToggle
}) => {
  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getStatusBadge = (status) => {
    const colors = {
      green: 'bg-emerald-100 text-emerald-700',
      amber: 'bg-amber-100 text-amber-700',
      red: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className={`rounded-xl border ${bgColor} overflow-hidden`}>
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/50 transition-colors"
        data-testid={`recommendation-${type}-toggle`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${iconColor}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500">
              {loading ? 'Loading...' : `${items?.length || 0} recommendations`}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3" data-testid={`recommendation-${type}-content`}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : error ? (
            <div className="text-center py-6 text-red-500">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">{error}</p>
            </div>
          ) : items?.length === 0 ? (
            <div className="text-center py-6 text-slate-400">
              <Info className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">No recommendations at this time</p>
            </div>
          ) : (
            items?.map((item, idx) => (
              <div
                key={item.project_id || item.programme_id || idx}
                className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow"
                data-testid={`recommendation-item-${idx}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900">
                      {item.project_name || item.programme_name}
                    </h4>
                    {item.programme_name && type !== 'funding' && (
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {item.programme_name}
                      </p>
                    )}
                    {type === 'funding' && item.project_count && (
                      <p className="text-sm text-slate-500">
                        {item.project_count} projects
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.status && (
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(item.status)}`}>
                        {item.status?.toUpperCase()}
                      </span>
                    )}
                    <span className={`text-lg font-bold ${getScoreColor(
                      item.stop_score || item.accelerate_score || item.value_score || item.funding_score || 0
                    )}`}>
                      {(item.stop_score || item.accelerate_score || item.value_score || item.funding_score || 0).toFixed(1)}
                    </span>
                  </div>
                </div>

                {/* Rationale */}
                {item.rationale && (
                  <p className="text-sm text-slate-600 mb-3 italic">
                    &ldquo;{item.rationale}&rdquo;
                  </p>
                )}

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  {type === 'stop' && (
                    <>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Net Savings</p>
                        <p className="font-semibold text-emerald-600">
                          {formatCurrency(item.net_savings || 0)}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Benefit Loss</p>
                        <p className="font-semibold text-red-600">
                          {formatCurrency(item.benefit_loss || 0)}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">FTE Released</p>
                        <p className="font-semibold text-blue-600">
                          {item.fte_released || 0}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Schedule Overrun</p>
                        <p className="font-semibold text-amber-600">
                          {item.factors?.schedule_overrun_pct || 0}%
                        </p>
                      </div>
                    </>
                  )}

                  {type === 'accelerate' && (
                    <>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Acceleration Cost</p>
                        <p className="font-semibold text-amber-600">
                          {formatCurrency(item.acceleration_cost || 0)}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Benefit Gain</p>
                        <p className="font-semibold text-emerald-600">
                          {formatCurrency(item.benefit_acceleration || 0)}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Max Weeks</p>
                        <p className="font-semibold text-blue-600">
                          {(item.max_acceleration_weeks || 0).toFixed(1)}w
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Health</p>
                        <p className={`font-semibold ${getScoreColor(item.factors?.health || 0)}`}>
                          {(item.factors?.health || 0).toFixed(0)}%
                        </p>
                      </div>
                    </>
                  )}

                  {type === 'value' && (
                    <>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Expected Benefit</p>
                        <p className="font-semibold text-emerald-600">
                          {formatCurrency(item.expected_benefit || 0)}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Benefit Realisation</p>
                        <p className={`font-semibold ${getScoreColor(item.factors?.benefit_realisation || 0)}`}>
                          {(item.factors?.benefit_realisation || 0).toFixed(0)}%
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Risk Adjusted</p>
                        <p className={`font-semibold ${getScoreColor(item.factors?.risk_adjusted || 0)}`}>
                          {(item.factors?.risk_adjusted || 0).toFixed(0)}%
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Alignment</p>
                        <p className={`font-semibold ${getScoreColor(item.factors?.strategic_alignment || 0)}`}>
                          {(item.factors?.strategic_alignment || 0).toFixed(0)}%
                        </p>
                      </div>
                    </>
                  )}

                  {type === 'funding' && (
                    <>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Current Investment</p>
                        <p className="font-semibold text-slate-700">
                          {formatCurrency(item.current_investment || 0)}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Recommended +</p>
                        <p className="font-semibold text-emerald-600">
                          +{formatCurrency(item.recommended_increase || 0)}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Delivery Health</p>
                        <p className={`font-semibold ${getScoreColor(item.factors?.delivery_health || 0)}`}>
                          {(item.factors?.delivery_health || 0).toFixed(0)}%
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded p-2">
                        <p className="text-slate-400 text-xs">Benefit Realisation</p>
                        <p className={`font-semibold ${getScoreColor(item.factors?.benefit_realisation || 0)}`}>
                          {(item.factors?.benefit_realisation || 0).toFixed(0)}%
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const ExecutiveRecommendationsPanel = ({ maxItems = 5 }) => {
  const { tenant, activeTenantId } = useTenant();
  const { formatCurrency, currencySymbol } = useCurrency();
  const tenantId = activeTenantId || tenant?.id;

  const [loading, setLoading] = useState({
    stop: false,
    accelerate: false,
    value: false,
    funding: false
  });
  
  const [errors, setErrors] = useState({
    stop: null,
    accelerate: null,
    value: null,
    funding: null
  });

  const [data, setData] = useState({
    stop: null,
    accelerate: null,
    value: null,
    funding: null
  });

  const [expanded, setExpanded] = useState({
    stop: true,
    accelerate: false,
    value: false,
    funding: false
  });

  const fetchRecommendations = async (type) => {
    if (!tenantId) return;
    
    setLoading(prev => ({ ...prev, [type]: true }));
    setErrors(prev => ({ ...prev, [type]: null }));

    try {
      let response;
      const headers = { 'X-Tenant-ID': tenantId };
      
      switch (type) {
        case 'stop':
          response = await api.get(`/scenario-intelligence/recommendations/what-should-stop?max_candidates=${maxItems}`, { headers });
          setData(prev => ({ ...prev, stop: response.data }));
          break;
        case 'accelerate':
          response = await api.get(`/scenario-intelligence/recommendations/what-should-accelerate?max_candidates=${maxItems}`, { headers });
          setData(prev => ({ ...prev, accelerate: response.data }));
          break;
        case 'value':
          response = await api.get(`/scenario-intelligence/recommendations/value-analysis?top_n=${maxItems}`, { headers });
          setData(prev => ({ ...prev, value: response.data }));
          break;
        case 'funding':
          response = await api.get(`/scenario-intelligence/recommendations/funding-priorities?top_n=${maxItems}`, { headers });
          setData(prev => ({ ...prev, funding: response.data }));
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(`Failed to fetch ${type} recommendations:`, err);
      setErrors(prev => ({ ...prev, [type]: err.message || 'Failed to load' }));
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const fetchAll = () => {
    fetchRecommendations('stop');
    fetchRecommendations('accelerate');
    fetchRecommendations('value');
    fetchRecommendations('funding');
  };

  useEffect(() => {
    if (tenantId) {
      fetchAll();
    }
  }, [tenantId]);

  const toggleExpanded = (type) => {
    setExpanded(prev => ({ ...prev, [type]: !prev[type] }));
    // Fetch if not loaded yet
    if (!data[type] && !loading[type]) {
      fetchRecommendations(type);
    }
  };

  return (
    <div className="space-y-4" data-testid="executive-recommendations-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Executive Recommendations</h2>
            <p className="text-sm text-slate-500">AI-powered strategic insights</p>
          </div>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
          disabled={Object.values(loading).some(l => l)}
          data-testid="refresh-recommendations"
        >
          <RefreshCw className={`w-4 h-4 ${Object.values(loading).some(l => l) ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Recommendation Cards */}
      <div className="grid gap-4">
        {/* What Should Stop */}
        <RecommendationCard
          title="What Should Stop"
          icon={StopCircle}
          iconColor="bg-red-500"
          bgColor="bg-red-50 border-red-200"
          items={data.stop?.candidates}
          loading={loading.stop}
          error={errors.stop}
          type="stop"
          formatCurrency={formatCurrency}
          currencySymbol={currencySymbol}
          expanded={expanded.stop}
          onToggle={() => toggleExpanded('stop')}
        />

        {/* What Should Accelerate */}
        <RecommendationCard
          title="What Should Accelerate"
          icon={Zap}
          iconColor="bg-emerald-500"
          bgColor="bg-emerald-50 border-emerald-200"
          items={data.accelerate?.candidates}
          loading={loading.accelerate}
          error={errors.accelerate}
          type="accelerate"
          formatCurrency={formatCurrency}
          currencySymbol={currencySymbol}
          expanded={expanded.accelerate}
          onToggle={() => toggleExpanded('accelerate')}
        />

        {/* Value Analysis - Most Valuable */}
        <RecommendationCard
          title="Most Valuable Initiatives"
          icon={TrendingUp}
          iconColor="bg-blue-500"
          bgColor="bg-blue-50 border-blue-200"
          items={data.value?.most_value}
          loading={loading.value}
          error={errors.value}
          type="value"
          formatCurrency={formatCurrency}
          currencySymbol={currencySymbol}
          expanded={expanded.value}
          onToggle={() => toggleExpanded('value')}
        />

        {/* Funding Priorities */}
        <RecommendationCard
          title="Needs More Funding"
          icon={DollarSign}
          iconColor="bg-amber-500"
          bgColor="bg-amber-50 border-amber-200"
          items={data.funding?.increase_funding}
          loading={loading.funding}
          error={errors.funding}
          type="funding"
          formatCurrency={formatCurrency}
          currencySymbol={currencySymbol}
          expanded={expanded.funding}
          onToggle={() => toggleExpanded('funding')}
        />
      </div>

      {/* Executive Summary */}
      {data.stop?.executive_summary && (
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 text-white">
          <div className="flex items-start gap-3">
            <Target className="w-5 h-5 text-violet-400 mt-0.5" />
            <div>
              <h3 className="font-semibold mb-1">Executive Summary</h3>
              <p className="text-sm text-slate-300">{data.stop.executive_summary}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutiveRecommendationsPanel;
